import { useState } from 'react';

import { Box, Text } from '@joinrepublic/dls-ui-kit';
import { observer } from 'mobx-react-lite';

import { INotificationOrderEnum } from '@r-client/data/graphql-types';
import { NotificationsIcon } from '@r-client/shared/ui/chakra-ui-icons';
import { Link, Tooltip } from '@r-client/shared/ui/core';

import { useGetNotificationsQuery } from '../../graphql/get-notifications-query';
import { NotificationsDropdown } from '../notifications-dropdown/notifications-dropdown';

export interface INotificationsDropdownMenuButtonProps {
  unreadNotificationsCount: number;
  mobile?: boolean;
  color?: string;
  onNotificationsClick?: VoidFunction;
}

export const NotificationsDropdownMenuButton = observer(
  function NotificationsDropdownMenuButton({
    unreadNotificationsCount,
    mobile = false,
    color = 'grey.900',
    onNotificationsClick,
  }: INotificationsDropdownMenuButtonProps) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const getNotificationsQuery = useGetNotificationsQuery({
      variables: { first: 30, order: INotificationOrderEnum.CreatedAtDesc },
    });

    const { data, isLoading } = getNotificationsQuery;

    // The control's only content is an icon and a count badge, so it has to be named explicitly
    // (axe `aria-command-name`, WCAG 4.1.2). The badge shows `99+` past a hundred; the name gives
    // the real number, which is what a screen reader user actually wants.
    const accessibleLabel = unreadNotificationsCount
      ? `Notifications, ${unreadNotificationsCount} unread`
      : 'Notifications';

    // A plain element rather than a nested component: an inline component declaration gets a new
    // type on every render, which would tear the icon and badge down each time.
    const notificationsIconContent = (
      <>
        <NotificationsIcon color={color} boxSize="6" />
        {!!unreadNotificationsCount && (
          <Text
            v="2.2"
            size="body-300"
            as="span"
            color="white"
            position="absolute"
            bottom="-5px"
            right="-10px"
            textAlign="center"
            px="1"
            py="px"
            fontWeight="bold"
            background="red.500"
            borderRadius="xl"
            height="17px"
            minW="22px"
            data-testid="notifications-count-badge"
          >
            {unreadNotificationsCount < 100 ? unreadNotificationsCount : '99+'}
          </Text>
        )}
      </>
    );

    if (mobile) {
      return (
        <Link
          href="/notifications"
          aria-label={accessibleLabel}
          variant={Link.variants['noUnderline']}
        >
          <Box
            position="relative"
            display="inline-block"
            data-testid="notifications-icon"
          >
            {notificationsIconContent}
          </Box>
        </Link>
      );
    }

    return (
      <NotificationsDropdown
        notifications={data?.notifications.edges.map((e) => e.node)}
        loading={isLoading}
        visibilitySetter={setDropdownOpen}
        visible={dropdownOpen}
      >
        {/*
          The button sits OUTSIDE the open/closed branch on purpose. The tooltip is dropped while
          the dropdown is open, and React remounts an element whose type changes at a given
          position — so wrapping the button in that branch would tear it down the moment the
          dropdown opens and drop keyboard focus on the floor. Keeping it stable also gives
          `NotificationsDropdown` a single element to hold its trigger ref on.
        */}
        <Box
          as="button"
          type="button"
          aria-label={accessibleLabel}
          // `aria-expanded` only, no `aria-haspopup`: `NotificationsDropdown` renders plain divs
          // with no dialog role, focus trap or Escape handling, so claiming `haspopup="dialog"`
          // would promise semantics the popup does not implement. This is the disclosure pattern.
          aria-expanded={dropdownOpen}
          position="relative"
          display="block"
          cursor="pointer"
          data-testid="notifications-icon"
          onClick={onNotificationsClick}
        >
          {dropdownOpen ? (
            notificationsIconContent
          ) : (
            <Tooltip content="Notifications" delayHide={0} placement="bottom">
              <span>{notificationsIconContent}</span>
            </Tooltip>
          )}
        </Box>
      </NotificationsDropdown>
    );
  }
);
